import type { CallStatus, CallsListQuery } from './types';

/**
 * Parse the `?status=...&minScore=...` query string into a typed CallsListQuery.
 * Pure function so it can be unit-tested without rendering React.
 */
export function parseCallsFiltersFromSearch(search: string): CallsListQuery {
  const params = new URLSearchParams(search);
  const page = parseNumber(params.get('page')) ?? 1;
  const limit = parseNumber(params.get('limit')) ?? 20;
  const status = (params.get('status') as CallStatus) || undefined;
  return {
    page,
    limit,
    status: isCallStatus(status) ? status : undefined,
    minScore: parseNumber(params.get('minScore')),
    maxScore: parseNumber(params.get('maxScore')),
    startedAfter: params.get('startedAfter') || undefined,
    startedBefore: params.get('startedBefore') || undefined,
    phoneNumber: params.get('phoneNumber') || undefined,
    customerName: params.get('customerName') || undefined,
    sortBy: 'startedAt',
    sortOrder: 'desc',
  };
}

/**
 * Serialize filters back to a URLSearchParams instance, dropping undefined/empty fields.
 * Page is always reset to 1 when filters change (callers can override afterwards).
 */
export function serializeCallsFilters(filters: CallsListQuery): URLSearchParams {
  const out = new URLSearchParams();
  if (filters.status) out.set('status', filters.status);
  if (filters.minScore !== undefined) out.set('minScore', String(filters.minScore));
  if (filters.maxScore !== undefined) out.set('maxScore', String(filters.maxScore));
  if (filters.startedAfter) out.set('startedAfter', filters.startedAfter);
  if (filters.startedBefore) out.set('startedBefore', filters.startedBefore);
  if (filters.phoneNumber) out.set('phoneNumber', filters.phoneNumber);
  if (filters.customerName) out.set('customerName', filters.customerName);
  out.set('page', String(filters.page ?? 1));
  out.set('limit', String(filters.limit ?? 20));
  return out;
}

function parseNumber(value: string | null | undefined): number | undefined {
  if (value === null || value === undefined || value === '') return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

function isCallStatus(value: string | undefined): value is CallStatus {
  return (
    value === 'PENDING' ||
    value === 'PROCESSING' ||
    value === 'COMPLETED' ||
    value === 'FAILED'
  );
}
